import React, { useEffect } from 'react'
import { Block, SylApi, SylController, SylPlugin } from '@syllepsis/adapter'
import { Fragment, Node as ProseMirrorNode, Slice } from 'prosemirror-model'
import { throttle, debounce } from 'lodash'
import {
  addAttrsByConfig,
  getFromDOMByConfig,
  IUserAttrsConfig,
  setDOMAttrByConfig,
} from '../../utils/tool'
import { EditorView } from 'prosemirror-view'
import { events } from '@/helpers/event-emitter'
import { ACTIONS } from '@/share/constants'
import { IllustrateBubble } from '../../component/illustrateMod'
import { v4 as uuidv4 } from 'uuid'
import { GetQueryValue, removeEmpty } from '@/utils/common'

interface IHeaderProps {
  addAttributes?: IUserAttrsConfig
}

interface IHeaderStructure {
  level: number
}

const NAME = 'header'

class Header extends Block<IHeaderStructure> {
  public name = NAME

  public tagName = (node: any) => `h${(node && node.attrs.level) || 1}`

  public parseFromDOM = (dom: HTMLElement) => {
    const level = dom.tagName.match(/\d/)
    if (!level) return false
    const attrs = {
      level: +level[0],
      illustrateHas: dom.getAttribute('illustrateHas') === 'true',
      illustrateHasId: dom.getAttribute('illustrateHasId'),
      headerId: dom.getAttribute('headerId') || uuidv4(),
    }
    const attrsComment = {
      level: +level[0],
      class: 'hasComment',
      headerId: dom.getAttribute('headerId') || uuidv4(),
    }
    getFromDOMByConfig(
      this.props.addAttributes,
      dom,
      dom.firstElementChild && dom.firstElementChild.tagName == 'COMMENT'
        ? attrsComment
        : attrs,
    )
    return dom.firstElementChild && dom.firstElementChild.tagName == 'COMMENT'
      ? attrsComment
      : attrs
  }

  constructor(editor: SylApi, props: IHeaderProps) {
    super(editor, props)
    if (props.addAttributes) {
      addAttrsByConfig(props.addAttributes, this)
    }
  }

  public textMatcher = [
    {
      matcher: /^(#{1,6})\s$/,
      handler(match: RegExpExecArray) {
        return {
          level: match[1].length,
        }
      },
    },
  ]

  public attrs = {
    level: {
      default: 1,
    },
    class: {
      default: 'headerItem',
    },
    illustrateHas: {
      default: false,
    },
    illustrateHasId: {
      default: 0,
    },
    isFocus: {
      default: false,
    },
    headerId: {
      default: '',
    },
  }

  public parseDOM = Array.from({ length: 6 }, (_, i) => ({
    tag: `h${i + 1}`,
    getAttrs: this.parseFromDOM,
  }))

  public toDOM = (node: ProseMirrorNode) => {
    let hasComment = false
    const markType = this.editor.view.state.schema.marks.comment
    const schema = this.editor.view.state.schema
    node.descendants((nodePara, pos, parent) => {
      const marksToRemove = nodePara.marks.find((mark) => {
        return (
          mark.type === markType && mark.attrs.class.includes('comment') !== -1
        )
      })
      const illustrateHasMarksToRemove = nodePara.marks.find((mark) => {
        return (
          mark.type === markType && mark.attrs.class.includes('comment') !== -1
        )
      })
      if (marksToRemove) {
        const newAttrs = { ...node.attrs, class: 'hasComment' }
        hasComment = true
        return false
      }

      return true
    })

    const attrs = {
      level: node.attrs.level,
      class:
        node.attrs.class ||
        (hasComment ? 'hasComment headerItem' : 'headerItem'),
      illustrateHas: node.attrs.illustrateHas == true,
      illustrateHasId:
        node.attrs.illustrateHasId == 0 ? 0 : node.attrs.illustrateHasId,
      isFocus: node.attrs.isFocus,
      headerId: node.attrs.headerId || uuidv4(), // Ensure headerId is not reset
    }

    setDOMAttrByConfig(this.props.addAttributes, node, attrs)

    return [
      'div',
      { class: 'headerBox' },
      [`h${attrs.level}`, { ...attrs }, ['span', 0]],
    ] as const
  }
}

class HeaderController extends SylController {
  private illustrateBubble: IllustrateBubble
  // 保存标题折叠信息
  private headerStates = []
  private oldHeaderStates = []
  // 确认初始化只执行一次
  private headerStatesFlag = true

  constructor(editor: SylApi, props) {
    super(editor, props)
    this.illustrateBubble = new IllustrateBubble(this.editor.root)
    events.on(ACTIONS.ILLUSTRATE_BUBBLE, this.illustrateHandle)
    events.on(
      ACTIONS.ILLUSTRATE_BUBBLE_SUBMIT,
      this.illustrateBubbleSubmitHandle,
    )
    events.on(
      ACTIONS.ILLUSTRATE_BUBBLE_EDIT_STATUS,
      this.illustrateBubbleEditStatus,
    )
    var editorDiv = document.getElementById('sylEditorBox')
    editorDiv.addEventListener('click', (event) => {
      // 判断点击事件是否发生在 myDiv 之外
      var myDiv = document.getElementById('illustrateWrapper')

      var modal = document.getElementsByClassName('arco-confirem-model')
      var targetNode = event.target as HTMLElement
      if (
        myDiv &&
        modal.length == 0 &&
        !myDiv.contains(targetNode) &&
        !targetNode.classList.contains('illustrateBtn') &&
        !targetNode.classList.contains('noIllustrateBtn') &&
        !targetNode.classList.contains('illustrateModalBtn') &&
        !targetNode.classList.contains('confirmCancelBtn') &&
        !targetNode.classList.contains('arco-modal-close-icon') &&
        !targetNode.classList.contains('arco-icon-close') &&
        targetNode.tagName != 'path'
      ) {
        this.illustrateBubble.hide()
      }
    })

    /**
     * 标题折叠代码
     * */ 
    // 监听用户回车
    // document.addEventListener('keyup', (event) => {
    //   if (event.key === 'Enter') {
    //     event.stopPropagation()

    //     // console.log('进入回车了！！！！！！！！！！！！！！！！！！！', this.headerStates)

    //     // 检查是否有任何 header 的 state 为 'collapsed'
    //     const isExpanded = this.headerStates.some(
    //       (item) => item.state === 'collapsed',
    //     )

    //     if (!isExpanded) return

    //     // 再展开所有折叠标题
    //     this.expandOrCollapseAllTitles('expanded')

    //     // 整理headerStates数据
    //     this.collationHeaderStates()

    //     // 添加鼠标移入、移出、标题图片点击事件
    //     this.addEventForHeaderItems()

    //     this.editor.focus()
    //   }
    // })

    // // Override updateState method to listen to editor updates 重写updateState方法以监听编辑器更新
    // const originalUpdateState = this.editor.view.updateState.bind(
    //   this.editor.view,
    // )
    // this.editor.view.updateState = (state) => {
    //   this.handleEditorUpdate(state, this.editor.view.state)
    //   originalUpdateState(state)
    // }
  }

  /**
   * 1、两个折叠标题，改变第二个级数的展开问题
   * 2、点击按钮消失问题
   * 3、回车问题
   * */

  // 监听编辑器更新回调
  handleEditorUpdate(newState, oldState) {
    const newHeaders = []
    const oldHeaders = []

    newState.doc.descendants((node, pos) => {
      if (node.type.name === 'header') {
        newHeaders.push({ node, pos })
      }
    })

    oldState.doc.descendants((node, pos) => {
      if (node.type.name === 'header') {
        oldHeaders.push({ node, pos })
      }
    })

    const hasHeaderChanged = (newHeaders, oldHeaders) => {
      if (newHeaders.length !== oldHeaders.length) return true

      for (let i = 0; i < newHeaders.length; i++) {
        if (!newHeaders[i].node.eq(oldHeaders[i].node)) {
          return true
        }
      }

      return false
    }

    if (
      hasHeaderChanged(newHeaders, oldHeaders) &&
      this.isHeaderStatesChanged()
      // || newState.doc.content.size !== oldState.doc.content.size
    ) {
      // console.log(
      //   'Header content updated',
      //   this.oldHeaderStates,
      //   this.headerStates,
      // )
      setTimeout(() => {
        this.TitleUpdateInlet()
      }, 0)

      // 存储之前的信息
      this.oldHeaderStates = this.headerStates
    } else if (
      hasHeaderChanged(newHeaders, oldHeaders) &&
      !this.isHeaderStatesChanged()
      // || newState.doc.content.size !== oldState.doc.content.size
    ) {
      // console.log('标题有变化，但未点击展开或折叠！！！！！！！')

      // 整理headerStates数据
      this.collationHeaderStates()

      // 添加鼠标移入、移出、标题图片点击事件
      this.addEventForHeaderItems()
    }
  }

  // 标题变化的回调
  TitleUpdateInlet = () => {
    console.log('监测到标题变化！！！！！！！！！！！！！！')
    // 更新新建标题 attrs 和 class
    this.updateHeaderAttrsAndClass()

    // 整理headerStates数据
    this.collationHeaderStates()

    // 添加鼠标移入、移出、标题图片点击事件
    this.addEventForHeaderItems()
  }

  /**
   * 整理headerStates数据并调整标题与图标关系
   * pos：需要修改的索引
   * state：展开或合并
   * level：标题级别
   */
  collationHeaderStates(
    pos?: number,
    state?: 'expanded' | 'collapsed',
    level?: number,
  ) {
    let titleContainer = document.getElementById('title-folding-content')
    const headerItems = Array.from(
      document.querySelectorAll('.ProseMirror .headerBox .headerItem'),
    )

    if (!titleContainer) {
      titleContainer = document.createElement('div')
      titleContainer.id = 'title-folding-content'
      document.querySelector('.syl-editor').appendChild(titleContainer)
    }

    // 将本地存储的状态写入headerStates
    if (this.headerStatesFlag) {
      // 初始化展开与合并，并将本地存储的状态写入headerStates
      this.initializeExpansionAndMerging()

      this.headerStatesFlag = false
    }

    // 去重
    this.headerStates = this.headerStates.filter(
      (item, index) =>
        this.headerStates.findIndex((i) => i.key === item.key) === index,
    )

    // 如果去除标题，展开所有标题
    if (headerItems.length < this.headerStates.length) {
      // 展开全部标题
      this.expandOrCollapseAllTitles('expanded')
    } else if (headerItems.length > this.headerStates.length) {
      // 如果标题级数被更改
      const headerItems = Array.from(
        document.querySelectorAll('.ProseMirror .headerBox .headerItem'),
      )
      headerItems.forEach((item, index) => {
        let itemLevel = item.getAttribute('level')
        if (
          this.headerStates[index] &&
          itemLevel !== this.headerStates[index].level
        ) {
          // 展开全部标题
          this.expandOrCollapseAllTitles('expanded')
        }
      })
    }

    // 重新整理数据
    if (!Number(pos) && !state && !Number(level)) {
      if (headerItems.length < this.headerStates.length) {
        // console.log('444444444444444444444444')

        for (let i = this.headerStates.length - 1; i >= 0; i--) {
          const state = this.headerStates[i]

          if (
            !headerItems.some(
              (header) => header.getAttribute('headerid') === state.key,
            )
          ) {
            this.headerStates.splice(i, 1)
          }
        }
      } else if (headerItems.length > this.headerStates.length) {
        // console.log('111111111111111111111111')

        headerItems.forEach((item, index) => {
          let itemHeaderId = item.getAttribute('headerid')
          let itemLevel = item.getAttribute('level')
          let itemHeaderStates = this.headerStates.find(
            (state) => state.key === itemHeaderId,
          )

          if (!itemHeaderStates) {
            // console.log('222222222222222222222222222222')

            this.headerStates.splice(index, 0, {
              key: itemHeaderId,
              state: 'expanded',
              level: itemLevel,
            })
          } else if (itemHeaderId !== itemHeaderStates.key) {
            // console.log('3333333333333333333333333')

            this.headerStates[index] = {
              key: itemHeaderId,
              state: 'expanded',
              level: itemLevel,
            }
          }
        })
      } else {
        // console.log('5555555555555555555555555555')

        headerItems.forEach((item, index) => {
          let itemHeaderId = item.getAttribute('headerid')
          let itemLevel = item.getAttribute('level')

          this.headerStates[index] = {
            ...this.headerStates[index],
            key: itemHeaderId,
            level: itemLevel,
          }
        })
      }
    } else {
      // console.log('7777777777777777777777777777')

      this.headerStates[pos] = { ...this.headerStates[pos], state, level }
    }

    // 清除所有的图标
    titleContainer.replaceChildren()

    // 根据 headerStates 创建图标
    this.headerStates.forEach((item, index) => {
      let containerInner = this.addContainerInner(
        headerItems[index],
        item.state,
        pos === index,
      )

      titleContainer.appendChild(containerInner)
    })

    // console.log(
    //   '数据已经更新！！！！！！！！！！！！！！！！！！',
    //   headerItems,
    //   titleContainer,
    //   this.headerStates,
    // )
  }

  // 添加鼠标移入、移出、标题图片点击事件
  addEventForHeaderItems() {
    const headerItems = Array.from(
      document.querySelectorAll('.ProseMirror .headerBox'),
    )
    let titleContainer = Array.from(
      document.querySelectorAll('#title-folding-content .title-folding-item'),
    ) as HTMLElement[]

    // console.log(
    //   '正在添加事件！！！！！！！！！！！！！！！！！！！！！',
    //   headerItems.length,
    //   this.headerStates.length,
    // )

    // 添加事件
    headerItems.forEach((item, index) => {
      let titleContainerItem = titleContainer[index]
      // 实现鼠标移入显示
      const showContainer = (event) => {
        event.stopPropagation()
        titleContainerItem.style.display = 'flex'

        // 鼠标移入重新计算图标高度
        const { top: editorTop } = this.editor.root.getBoundingClientRect()
        const { top: elementTop, height: itemHeight } =
          item.getBoundingClientRect()
        const computedTop = elementTop - editorTop
        titleContainerItem.style.top = computedTop + 'px'
        titleContainerItem.style.height = itemHeight + 20 + 'px'
      }

      // 实现鼠标移出隐藏
      const hideContainer = (event: MouseEvent) => {
        const e = event as MouseEvent
        if (
          !item.contains(e.relatedTarget as Node) &&
          !titleContainerItem.contains(e.relatedTarget as Node)
        ) {
          titleContainerItem.style.display = 'none'
        }
      }

      // 保存每个标题展开与隐藏的状态
      const toggleContent = (event) => {
        event.stopPropagation()
        let { state, level } = this.headerStates[index]

        if (state === 'expanded') {
          // console.log('正在进行折叠！！！！！！！！！！！！！！！！')

          this.collationHeaderStates(index, 'collapsed', level)
          this.expandOrMergeHand('collapsed', index)
        } else {
          // console.log('正在进行展开！！！！！！！！！！！！！！！！')

          this.collationHeaderStates(index, 'expanded', level)

          this.expandOrMergeHand('expanded', index)
          this.headerStates.forEach((item, i) => {
            if (item.state === 'collapsed') {
              this.expandOrMergeHand('collapsed', i)
            }
          })
        }
      }

      item.addEventListener('mouseenter', showContainer)
      item.addEventListener('mouseleave', hideContainer)
      titleContainerItem?.addEventListener('mouseenter', showContainer)
      titleContainerItem?.addEventListener('mouseleave', hideContainer)
      titleContainerItem?.addEventListener('click', toggleContent)
    })
  }

  // 点击合并/展开回调
  expandOrMergeHand = (state, index) => {
    const view = this.editor.view
    const { doc, tr } = view.state
    const headerItems = document.querySelectorAll(
      '.ProseMirror .headerBox .headerItem',
    )

    const headerElement = headerItems[index]

    if (!headerElement) return
    const headerId = headerElement.getAttribute('headerId')

    const updateAttrs = (attrs, action) => {
      let classList = attrs.class ? attrs.class.split(' ') : []

      if (action === 'collapse') {
        if (attrs.headerId && !classList.includes('collapsed')) {
          classList.push('collapsed') // Add 'collapsed' class
        }
        if (!attrs.headerId || attrs.headerId !== headerId) {
          if (!classList.includes('title-folding-hidden')) {
            classList.push('title-folding-hidden') // Add 'title-folding-hidden' class if it's not the current header
          }
        }
      } else {
        classList = classList.filter(
          (cls) => cls !== 'collapsed' && cls !== 'title-folding-hidden',
        ) // Remove 'collapsed' and 'title-folding-hidden' classes
      }

      return { ...attrs, class: classList.join(' ') }
    }

    const updateHeaderAndDescendants = (node, pos, action) => {
      const newAttrs = updateAttrs(node.attrs, action)
      tr.setNodeMarkup(pos, null, newAttrs)

      const endPos = this.findNextHeaderPos(pos, node.attrs.level)
      let stopTraversal = false

      doc.nodesBetween(pos + 1, endPos, (descendantNode, descendantPos) => {
        // 使用 view.nodeDOM 获取当前节点
        const specialElement = view.nodeDOM(descendantPos) as HTMLElement

        if (
          JSON.stringify(descendantNode.attrs) !== '{}' ||
          descendantNode.type.name === 'table' ||
          descendantNode.type.name === 'block_quote'
        ) {
          if (stopTraversal) return false

          // console.log(descendantNode, descendantNode.textContent)

          if (descendantNode.type.name === 'header') {
            // 如果是标题，并且比当前标题级别低，则折叠
            if (
              descendantNode.attrs.level <= node.attrs.level &&
              descendantPos !== pos
            ) {
              stopTraversal = true
              return false
            }

            const descendantAttrs = updateAttrs(descendantNode.attrs, action)
            tr.setNodeMarkup(descendantPos, null, descendantAttrs)

            // 查找 headerBox 元素
            const nodeElement = view.domAtPos(descendantPos).node as HTMLElement
            const headerBoxElement = this.findHeaderBoxElement(nodeElement)

            if (headerBoxElement) {
              headerBoxElement.classList.toggle(
                'collapsed',
                action === 'collapse',
              )
            }
          } else if (descendantNode.type.name === 'block_quote') {
            // 如果是引用块，则折叠
            const descendantAttrs = updateAttrs(descendantNode.attrs, action)
            tr.setNodeMarkup(descendantPos, null, descendantAttrs)

            return false
          } else if (descendantNode.type.name === 'list_item') {
            const descendantAttrs = updateAttrs(descendantNode.attrs, action)
            tr.setNodeMarkup(descendantPos, null, descendantAttrs)

            return false
          } else if (descendantNode.type.name === 'code_block') {
            // 如果是代码块，则折叠
            let specialElementLastChild =
              specialElement.lastChild as HTMLElement

            specialElementLastChild.style.display =
              action === 'collapse' ? 'none' : ''

            return false
          } else if (
            descendantNode.type.name === 'video' ||
            descendantNode.type.name === 'audio'
          ) {
            requestAnimationFrame(() => {
              specialElement.style.display = action === 'collapse' ? 'none' : ''
            })

            return false
          } else if (descendantNode.type.name === 'image') {
            requestAnimationFrame(() => {
              specialElement.style.display = action === 'collapse' ? 'none' : ''
            })

            return false
          } else {
            const descendantAttrs = updateAttrs(descendantNode.attrs, action)
            tr.setNodeMarkup(descendantPos, null, descendantAttrs)
          }
        }

        if (descendantNode.type.name === 'table') {
          let specialElementLastChild = specialElement.lastChild as HTMLElement
          specialElementLastChild.style.display =
            action === 'collapse' ? 'none' : ''

          return false
        } else if (specialElement && specialElement.tagName === 'HR') {
          requestAnimationFrame(() => {
            action === 'collapse'
              ? specialElement.classList.add('title-folding-hidden')
              : specialElement.classList.remove('title-folding-hidden')
          })

          return false
        }

        return true
      })
    }

    doc.descendants((node, pos) => {
      if (node.type.name === 'header') {
        if (node.attrs.headerId === headerId) {
          // 显示或隐藏
          updateHeaderAndDescendants(
            node,
            pos,
            state === 'expanded' ? 'expand' : 'collapse',
          )
          // 修改状态
          this.headerStates[index].state = state
          return false // Stop outer iteration
        }
      }
      return true
    })

    // Force the view to update
    // view.updateState(view.state.apply(tr))
    view.dispatch(tr)
    // this.editor.undo()
  }

  // Helper function to find the position of the next header of the same or higher level 辅助函数，用于查找相同或更高级别的下一个标题的位置
  findNextHeaderPos = (startPos, level) => {
    const { doc } = this.editor.view.state
    let endPos = doc.content.size

    doc.descendants((node, pos) => {
      if (
        pos > startPos &&
        node.type.name === 'header' &&
        node.attrs.level <= level
      ) {
        endPos = pos
        return false // Stop iteration
      }
      return true
    })

    return endPos
  }

  // Helper function to find the closest headerBox element  辅助函数，用于查找最近的headerBox元素
  findHeaderBoxElement = (node: Node): HTMLElement | null => {
    if (!(node instanceof HTMLElement)) return null

    if (node.classList.contains('headerBox')) {
      return node
    }

    if (node.parentElement) {
      return this.findHeaderBoxElement(node.parentElement)
    }

    return null
  }

  // 更新新建标题 attrs 和 class
  updateHeaderAttrsAndClass = () => {
    const view = this.editor.view
    const { doc, tr } = view.state

    // 遍历所有段落节点
    doc.descendants((node, pos) => {
      // 获取 ProseMirror 节点在 DOM 中的表示
      const domNode = view.nodeDOM(pos)

      if (node.type.name === 'paragraph') {
        if (domNode instanceof HTMLElement) {
          // 从 DOM 中获取类
          const domClassList = domNode.getAttribute('class') || ''
          const classArray = domClassList.split(' ')

          // 更新节点的 class 属性
          const updatedAttrs = {
            ...node.attrs,
            class: classArray.join(' '),
          }

          tr.setNodeMarkup(pos, null, updatedAttrs)
        }
      } else if (node.type.name === 'header') {
        const domClassList =
          (domNode.childNodes[0] as HTMLElement).getAttribute('class') || ''
        const classArray = domClassList.split(' ')

        // console.log('+====================', classArray)

        // 防止标题中出现 title-folding-hidden class
        // const classArray = domClassList
        //   .split(' ')
        //   .filter((className) => className !== 'title-folding-hidden')

        // if (classArray.includes('title-folding-hidden')) {
        //   classArray.splice(classArray.indexOf('title-folding-hidden'), 1)
        // }

        // 更新节点的 class 属性
        const updatedAttrs = {
          ...node.attrs,
          class: classArray.join(' '),
          headerId: node.attrs.headerId || uuidv4(),
        }

        tr.setNodeMarkup(pos, null, updatedAttrs)
      }
      return true
    })

    // 更改保存信息，需要延迟存储，防止编辑器未保存
    setTimeout(() => {
      this.storeOrReturnFoldedData(GetQueryValue('uuid'), this.headerStates)
    }, 500)

    // 提交更新
    view.dispatch(tr)
  }

  // 初始化时回到之前的展开/合并状态
  initializeExpansionAndMerging = () => {
    const data = this.storeOrReturnFoldedData(GetQueryValue('uuid'))
    if (!data) return

    this.headerStates = data

    data &&
      data.forEach((item, i) => {
        if (item.state === 'collapsed') {
          this.expandOrMergeHand('collapsed', i)
        }
      })

    // 检查是否有任何 header 的 state 为 'collapsed'
    const isExpanded = data.some((item) => item.state === 'collapsed')
    // 防止图标为展开，但是标题被隐藏
    if (
      !isExpanded &&
      document.querySelectorAll('.title-folding-hidden').length
    ) {
      // 再展开所有折叠标题
      this.expandOrCollapseAllTitles('expanded')
      this.editor.focus()
    }
  }

  /**
   * 创建图标容器
   * headerItem：标题元素
   * action：展开/合并
   * pos：第几个图标不隐藏
   */
  addContainerInner = (
    headerItem: Element,
    action: 'expanded' | 'collapsed',
    isHide?: boolean,
  ) => {
    let itemHeaderId = headerItem.getAttribute('headerid')
    let itemLevel = headerItem.getAttribute('level')

    const containerInner = document.createElement('div')
    containerInner.id = `title-folding-item-${itemLevel}`
    containerInner.classList.add('title-folding-item')
    containerInner.setAttribute('state', action)
    containerInner.setAttribute('key', itemHeaderId)

    const { top: editorTop } = this.editor.root.getBoundingClientRect()
    const { top: elementTop, height: itemHeight } =
      headerItem.getBoundingClientRect()
    const computedTop = elementTop - editorTop
    containerInner.innerHTML =
      action === 'expanded'
        ? '<svg height="24" width="24" data-name="图层 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M38,14.17H10c-.41,0-.65,.47-.4,.8l13.87,18.67c.2,.27,.6,.27,.8,0l14.13-18.67c.25-.33,.01-.8-.4-.8Z"/></svg>'
        : '<svg height="24" width="24" data-name="图层 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M14.17,10v28c0,.41,.47,.65,.8,.4l18.67-13.87c.27-.2,.27-.6,0-.8L14.97,9.6c-.33-.25-.8-.01-.8,.4Z"/></svg>'

    if (isHide) {
      containerInner.style.display = 'flex'
    } else {
      containerInner.style.display = 'none'
    }
    containerInner.style.alignItems = 'center'
    containerInner.style.position = 'absolute'
    containerInner.style.top = computedTop + 'px'
    containerInner.style.width = '50px'
    containerInner.style.height = itemHeight + 20 + 'px'
    containerInner.style.left = '-50px'
    containerInner.style.marginTop = '-10px'
    containerInner.style.cursor = 'pointer'

    return containerInner
  }

  // 展开或折叠所有标题
  expandOrCollapseAllTitles = (action: 'expanded' | 'collapsed') => {
    // 防止继续设置为标题，导致标题隐藏，数据丢失

    // 展开
    if (action === 'expanded') {
      // this.expandOrMergeHand('expanded', index)
      this.headerStates.forEach((item, i) => {
        if (item.state === 'expanded') {
          this.expandOrMergeHand('collapsed', i)
          item.state = 'collapsed'
        }
      })
      this.headerStates.forEach((item, i) => {
        if (item.state === 'collapsed') {
          this.expandOrMergeHand('expanded', i)
          item.state = 'expanded'
        }
      })
    } else {
      this.headerStates.forEach((item, i) => {
        if (item.state === 'collapsed') {
          this.expandOrMergeHand('expanded', i)
          item.state = 'expanded'
        }
      })
      this.headerStates.forEach((item, i) => {
        if (item.state === 'expanded') {
          this.expandOrMergeHand('collapsed', i)
          item.state = 'collapsed'
        }
      })
    }
    this.editor.blur()
  }

  /**
   * 更新存储
   * uuid：当前文档uuid
   * headerStates：需要保存的数据，如果有此值，则为存储，如果没有此值，则返回uuid对应的数据
   */
  storeOrReturnFoldedData = (uuid, headerStates = null) => {
    try {
      const titleMergeStatus =
        JSON.parse(localStorage.getItem('Title-Merge-Status')) || []

      if (headerStates) {
        // 存储数据
        let itemFound = false
        const updatedStatus = titleMergeStatus.map((item) => {
          if (item.key === uuid) {
            itemFound = true
            return {
              ...item,
              value: headerStates,
            }
          }
          return item
        })

        if (!itemFound) {
          updatedStatus.push({
            key: uuid,
            value: headerStates,
          })
        }

        localStorage.setItem(
          'Title-Merge-Status',
          JSON.stringify(updatedStatus),
        )
      } else {
        // 获取数据
        const matchedItem = titleMergeStatus.find((item) => item.key === uuid)
        return matchedItem ? matchedItem.value : null
      }
    } catch (error) {
      console.error('Failed to handle Title-Merge-Status:', error)
      if (headerStates) {
        const updatedStatus = [
          {
            key: uuid,
            value: headerStates,
          },
        ]
        localStorage.setItem(
          'Title-Merge-Status',
          JSON.stringify(updatedStatus),
        )
      }
      return null
    }
  }

  // 获取光标在第几个标题
  findFocusedHeaderIndex() {
    const { doc, selection } = this.editor.view.state
    const { from, to } = selection

    // 如果选择区域包含多节点
    if (from !== to) {
      return -1 // 或者其他逻辑处理
    }

    let focusedHeaderIndex = -1
    let currentIndex = 0

    doc.descendants((node, pos) => {
      if (node.type.name === 'header') {
        // 确保 'header' 是你定义的标题类型名称
        const start = pos
        const end = pos + node.nodeSize

        // Check if selection is within the current node
        if (from >= start && to <= end) {
          focusedHeaderIndex = currentIndex
          return false // Stop traversing
        }
        currentIndex++
      }
      return true // Continue traversing
    })

    return focusedHeaderIndex
  }

  /**
   *  判断headerStates的state是否有变化，如果this.oldHeaderStates为空也返回true
   *  @returns
   *  true：有变化
   *  false：无变化
   */
  isHeaderStatesChanged() {
    if (this.oldHeaderStates.length === 0) {
      return true
    }

    let isChange = false
    this.headerStates.forEach((item, i) => {
      if (item.state !== this.oldHeaderStates[i].state) isChange = true
    })

    return isChange
  }

  editorWillUnmount() {
    events.off(ACTIONS.ILLUSTRATE_BUBBLE, this.illustrateHandle)
    events.off(
      ACTIONS.ILLUSTRATE_BUBBLE_SUBMIT,
      this.illustrateBubbleSubmitHandle,
    )
    events.off(
      ACTIONS.ILLUSTRATE_BUBBLE_EDIT_STATUS,
      this.illustrateBubbleEditStatus,
    )
    // events.off(ACTIONS.ILLUSTRATE_BUBBLE_HIDE, this.illustrateBubbleHide)
  }

  public illustrateBubbleHide = () => {
    this.illustrateBubble.hide()
  }

  public illustrateBubbleEditStatus = (payload) => {
    const { nodePos, paragraphSign } = payload
    const view = this.editor.view
    this.editor.view.state.doc.descendants((node, pos) => {
      if (node.type.name == 'header') {
        if (node.attrs.illustrateHasId == paragraphSign) {
          const newAttrs = {
            ...node.attrs,
            illustrateHas: false,
          }
          const tr = view.state.tr.setNodeMarkup(pos, null, newAttrs)
          view.dispatch(tr)
        }
      }

      return true
    })

    this.illustrateBubble.hide()
    // events.emit(ACTIONS.ILLUSTRATE_BUBBLE_HIDE, { nodePos: nodePos })
  }

  public illustrateBubbleSubmitHandle = (payload) => {
    const { nodePos, illustrateHasId } = payload
    const currentNode = this.editor.view.state.doc.nodeAt(nodePos)
    const view = this.editor.view
    const newAttrs = {
      ...currentNode.attrs,
      illustrateHas: true,
      illustrateHasId: illustrateHasId,
    }
    const tr = view.state.tr.setNodeMarkup(nodePos, null, newAttrs)
    view.dispatch(tr)
  }

  public illustrateHandle = (payload) => {
    const { pos, attrs } = payload
    this.editor.view.state.tr.setNodeMarkup(pos, null, attrs)
    this.editor.view.dispatch(this.editor.view.state.tr)
  }

  public name = NAME

  // 处理修改属性的逻辑
  private modifyAttributes(fragment: Fragment): Fragment {
    const modifiedNodes: ProseMirrorNode[] = []

    // 遍历 fragment 中的每一个节点
    fragment.forEach((node) => {
      // 检查是否是 header 标签
      if (node.type.name === 'header' && node.attrs) {
        // 修改 attrs 属性
        const modifiedAttrs = {
          ...node.attrs,
          illustrateHas: false,
          illustrateHasId: 0,
        }
        // 创建一个新的节点，带有修改后的 attrs
        const modifiedNode = node.type.createAndFill(
          modifiedAttrs,
          node.content,
          node.marks,
        )
        if (modifiedNode) {
          modifiedNodes.push(modifiedNode)
        }
      } else {
        // 其他节点不变
        modifiedNodes.push(node)
      }
    })

    return Fragment.fromArray(modifiedNodes)
  }

  public eventHandler = {
    handleClickOn: (
      editor: SylApi,
      view: EditorView,
      pos: number,
      node: ProseMirrorNode,
      nodePos: number,
      event: MouseEvent,
    ) => {
      if (!editor.editable) return false
      event.preventDefault()
      const currentAttrs = node.attrs
      const illustrateHasId =
        node.attrs.illustrateHasId == 0
          ? 'illustrateHasId-' + Date.now()
          : node.attrs.illustrateHasId
      const clickedElement = event.target as HTMLElement
      const { top: editorTop } = this.editor.root.getBoundingClientRect()
      const { left: headLeft, bottom: headBottom } =
        this.editor.view.coordsAtPos(nodePos)
      const computedTop = headBottom + 8 - editorTop
      //这里掉接口  判断有没有说明
      const newAttrs = {
        ...currentAttrs,
        isFocus: !currentAttrs.illustrateHas,
      }

      if (clickedElement.classList.contains('illustrateBtn')) {
        this.illustrateBubble.show(
          computedTop,
          illustrateHasId,
          this.editor,
          true,
          nodePos,
        )
      } else if (clickedElement.classList.contains('noIllustrateBtn')) {
        this.illustrateBubble.show(
          computedTop,
          illustrateHasId,
          this.editor,
          false,
          nodePos,
        )
      } else if (clickedElement.classList.contains('hasComment')) {
        events.emit(ACTIONS.COMMENT_BUBBLE_HEADER_SHOW, {
          pos: nodePos,
          currentNode: node,
        })
      }

      const state = view.state

      // 获取文档的根节点
      const doc = state.doc

      // 获取编辑器的 schema
      const schema = state.schema

      // 新的标题节点类型（假设为 'heading'）
      const headerType = schema.nodes.header
      let focusPos = -1
      let focusAttrs

      const buttonGroup = document.querySelector('.button-group')
      buttonGroup.setAttribute('pos', String(nodePos))
      const markType = this.editor.view.state.schema.marks.comment

      doc.descendants((node, pos) => {
        // 判断当前节点是否为标题节点
        if (node.type === headerType) {
          // 获取当前节点的 attrs
          const currentAttrs = node.attrs
          // 修改 attrs，这里仅为示例，你可以根据实际需求修改 attrs 的内容
          if (currentAttrs.isFocus) {
            focusPos = pos
            focusAttrs = currentAttrs
            return false
          }
        }

        return true
      })
      let hasListComment
      node.descendants((node, pos) => {
        const marksToRemove = node.marks.find((mark) => {
          return (
            mark.type === markType &&
            mark.attrs.class.includes('comment') !== -1
          )
        })
        if (marksToRemove) {
          hasListComment = true
          return true
        }

        return true
      })

      buttonGroup.setAttribute(
        'style',
        `top:${computedTop}px; width: 24px;
      display:${hasListComment ? 'none' : 'block'};
      position:absolute;
      height: 24px;
      right: -50px;
      padding: 10px;
      cursor: pointer;
      background-size: 18px;
      border-radius: 4px;
      background-position: center;
      border: 1px solid #e0e0e0;
      background-repeat: no-repeat;
      background-image: url('https://open.v-dk.com/aidb/wp-content/uploads/sites/10/2024/01/addComment.svg');
      pointer-events: auto;`,
      )
      if (focusPos < 0) {
        const tr = state.tr.setNodeMarkup(nodePos, null, {
          ...currentAttrs,
          isFocus: true,
        })
        view.dispatch(tr)
      } else {
        const tr = state.tr
          .setNodeMarkup(nodePos, null, { ...currentAttrs, isFocus: true })
          .setNodeMarkup(focusPos, null, { ...focusAttrs, isFocus: false })
        view.dispatch(tr)
      }

      return false
    },
    transformPasted: (editor: SylApi, slice: Slice) => {
      const newSlice = new Slice(
        this.modifyAttributes(slice.content),
        slice.openStart,
        slice.openEnd,
      )
      return newSlice
    },
  }

  // 展开当前标题
  public expandCurrentTitleKeyMap = () => {
    let currentFocusedIndex = this.findFocusedHeaderIndex()
    if (currentFocusedIndex > -1) {
      this.expandOrMergeHand('expanded', currentFocusedIndex)

      // 整理headerStates数据
      this.collationHeaderStates()

      // 添加鼠标移入、移出、标题图片点击事件
      this.addEventForHeaderItems()

      this.editor.focus()
    }
  }

  // 折叠当前标题
  public collapsCurrentTitleKeyMap = () => {
    let currentFocusedIndex = this.findFocusedHeaderIndex()
    if (currentFocusedIndex > -1) {
      this.expandOrMergeHand('collapsed', currentFocusedIndex)

      // 整理headerStates数据
      this.collationHeaderStates()

      // 添加鼠标移入、移出、标题图片点击事件
      this.addEventForHeaderItems()

      this.editor.focus()
    }
  }

  // 展开所有标题
  public expandAllTitleKeyMap = () => {
    // 再展开全部标题
    this.expandOrCollapseAllTitles('expanded')
    this.editor.focus()
  }

  // 折叠所有标题
  public collapsAllTitleKeyMap = () => {
    // 先合并全部标题
    this.expandOrCollapseAllTitles('collapsed')
    this.editor.focus()
  }

  // 定义快捷键
  // public keymap = {
  //   // 展开当前标题
  //   'ctrl-l': this.expandCurrentTitleKeyMap.bind(this), // launch
  //   // 折叠当前标题
  //   'ctrl-f': this.collapsCurrentTitleKeyMap.bind(this), // fold
  //   // 展开所有标题
  //   'ctrl-shift-l': this.expandAllTitleKeyMap.bind(this),
  //   // 折叠所有标题
  //   'ctrl-shift-f': this.collapsAllTitleKeyMap.bind(this),
  // }

  // public keymap = {
  //   Enter: (_editor: SylApi, state: SylApi['view']['state']) => {
  //     console.log('pppp')
  //     const headerType = state.schema.nodes.header
  //     const { $from, $to } = state.selection

  //     console.log($from.pos, $from.before($from.depth), _editor.view.state.doc)
  //     if ($from.parent.type.name === 'header') {
  //       const modifiedAttrs = {
  //         ...$from.parent.attrs,
  //         illustrateHas: false,
  //         illustrateHasId: 0,
  //       }
  //       const tr = state.tr.setNodeMarkup($from.before($from.depth), null, {
  //         ...$from.parent.attrs,
  //         illustrateHas: false,
  //         illustrateHasId: 0,
  //       })

  //       _editor.view.dispatch(tr)
  //     }
  //     return false
  //   },
  // }

  public toolbar = {
    className: 'header',
    tooltip: 'header',
    type: 'select',
    value: [
      {
        attrs: false,
      },
      ...Array.from({ length: 6 }, (_, i) => ({
        text: `H${i + 1}`,
        attrs: { level: i + 1 },
      })),
    ],
  }
}

class HeaderPlugin extends SylPlugin<IHeaderProps> {
  public name = NAME
  public Controller = HeaderController
  public Schema = Header
}

export { Header, HeaderController, HeaderPlugin }
